import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./Shopfront.module.css";
import { useCart } from "../../Context/CartContext";

const CATEGORIES = ["All", "Orthopedic", "Diagnostics", "Wellness", "Nutrition", "Rehabilitation"];

const ProductCard = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const [wished, setWished] = useState(false);
  const [added, setAdded] = useState(false);

  const handleAdd = (e) => {
    e.stopPropagation();
    onAddToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const handleWishlist = (e) => {
    e.stopPropagation();
    setWished(!wished);
  };

  return (
    <div className={styles.productCard} onClick={() => navigate(`/shop/product/${product._id}`)}>
      <div className={styles.imageArea}>
        <img
          src={product.coverImage || "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=60"}
          alt={product.title}
          className={styles.productImage}
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=60"; }}
        />
        <button
          className={`${styles.wishlistBtn} ${wished ? styles.wishlistActive : ""}`}
          onClick={handleWishlist}
          title={wished ? "Remove from wishlist" : "Add to wishlist"}
        >
          {wished ? "♥" : "♡"}
        </button>
      </div>
      <div className={styles.contentArea}>
        <div>
          <p className={styles.categoryLabel}>{product.category}</p>
          <h3 className={styles.productTitle}>{product.title}</h3>
        </div>
        <div className={styles.bottomRow}>
          <div className={styles.priceWrap}>
            <span className={styles.priceMain}>₹{(product.price * 83).toFixed(0)}</span>
            <span className={styles.priceSub}>${product.price.toFixed(2)} USD</span>
          </div>
          {product.stockQty > 0 ? (
            <button
              className={`${styles.addBtn} ${added ? styles.addBtnSuccess : ""}`}
              onClick={handleAdd}
              title="Add to Cart"
            >
              {added ? "✓" : "+"}
            </button>
          ) : (
            <span style={{ fontSize: "0.72rem", color: "#ef4444", fontWeight: 700 }}>Out of Stock</span>
          )}
        </div>
      </div>
    </div>
  );
};

const Shopfront = () => {
  const navigate = useNavigate();
  const { addToCart, cartCount } = useCart();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("createdAt");

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category !== "All") params.category = category;
      if (sort) params.sort = sort;
      const res = await axios.get("/api/shop/products", { params });
      setProducts(res.data.products || []);
    } catch (err) {
      console.error("Failed to fetch products", err);
    } finally {
      setLoading(false);
    }
  }, [search, category, sort]);

  useEffect(() => {
    const timer = setTimeout(fetchProducts, 350);
    return () => clearTimeout(timer);
  }, [fetchProducts]);

  return (
    <div className={styles.pageContainer}>
      {/* 1. The Sticky Header (Search & Cart) */}
      <header className={styles.stickyHeader}>
        <button
          className={styles.glassHomeBtn}
          onClick={() => navigate("/patient-dashboard")}
          title="Back to Patient Dashboard"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: "drop-shadow(0 2px 4px rgba(45,106,79,0.25))" }}>
            <path d="M3 10.25L12 3L21 10.25V20C21 20.5523 20.5523 21 20 21H15V15H9V21H4C3.44772 21 3 20.5523 3 20V10.25Z" fill="url(#homeGradient)" stroke="#1e293b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <defs>
              <linearGradient id="homeGradient" x1="3" y1="3" x2="21" y2="21" gradientUnits="userSpaceOnUse">
                <stop stopColor="#6ee7b7" />
                <stop offset="1" stopColor="#10b981" />
              </linearGradient>
            </defs>
          </svg>
          <span className={styles.glassHomeText}>Dashboard</span>
        </button>

        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="7" fill="url(#searchGrad)" stroke="#64748b" strokeWidth="2" />
              <path d="M20 20L16.5 16.5" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" />
              <defs>
                <linearGradient id="searchGrad" x1="4" y1="4" x2="18" y2="18" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#ffffff" stopOpacity="0.9" />
                  <stop offset="1" stopColor="#cbd5e1" stopOpacity="0.4" />
                </linearGradient>
              </defs>
            </svg>
          </span>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search wellness supplies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button className={styles.cartBtn} onClick={() => navigate("/shop/checkout")} title="Shopping Cart">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: "drop-shadow(0 2px 4px rgba(15,23,42,0.12))" }}>
            <path d="M6 6H20L18.5 14H7.5L6 6Z" fill="url(#cartGrad)" stroke="#1e293b" strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M6 6L4 3H2" stroke="#1e293b" strokeWidth="1.6" strokeLinecap="round"/>
            <circle cx="9" cy="18" r="2" fill="#2d6a4f" />
            <circle cx="17" cy="18" r="2" fill="#2d6a4f" />
            <defs>
              <linearGradient id="cartGrad" x1="6" y1="6" x2="20" y2="14" gradientUnits="userSpaceOnUse">
                <stop stopColor="#a7f3d0" />
                <stop offset="1" stopColor="#34d399" />
              </linearGradient>
            </defs>
          </svg>
          {cartCount > 0 && <span className={styles.cartBadge}>{cartCount}</span>}
        </button>
      </header>

      {/* 2. The Trust Strip (Micro-text) */}
      <div className={styles.trustStrip}>
        <div className={styles.trustText}>
          We wish your wellness !
        </div>
      </div>

      {/* Sleek Compact Hero Banner */}
      <section className={styles.heroBanner}>
        <span className={styles.heroTag}>ReformMe Shop</span>
        <h1 className={styles.heroTitle}>Curated Healthcare & Wellness</h1>
        <p className={styles.heroSubtitle}>
          Doctor-recommended orthopedic supports, diagnostics tools, and everyday wellness supplies.
        </p>
      </section>

      {/* 3. The Category "Chip" Row (Horizontal Scroll - CRITICAL) */}
      <div className={styles.categoryRow}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`${styles.chip} ${category === cat ? styles.chipActive : ""}`}
            onClick={() => setCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Grid Toolbar / Section Header */}
      <div className={styles.gridToolbar}>
        <h2 className={styles.gridTitle}>
          {category === "All" ? "All Products" : `${category} Products`} ({products.length})
        </h2>
        <select className={styles.sortSelect} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="createdAt">Newest</option>
          <option value="price_asc">Price: Low → High</option>
          <option value="price_desc">Price: High → Low</option>
          <option value="rating">Top Rated</option>
        </select>
      </div>

      {/* 4. The Product Grid */}
      <main>
        {loading ? (
          <div className={styles.stateContainer}>
            <div className={styles.stateIcon}>⏳</div>
            <h3 className={styles.stateTitle}>Loading products...</h3>
            <p style={{ fontSize: "0.85rem" }}>Curating premium wellness supplies for you.</p>
          </div>
        ) : products.length === 0 ? (
          <div className={styles.stateContainer}>
            <div className={styles.stateIcon}>🔍</div>
            <h3 className={styles.stateTitle}>No products found</h3>
            <p style={{ fontSize: "0.85rem" }}>Try adjusting your search query or selecting a different category.</p>
          </div>
        ) : (
          <div className={styles.productGrid}>
            {products.map((p) => (
              <ProductCard key={p._id} product={p} onAddToCart={(product) => addToCart(product, 1)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Shopfront;
